import { motion } from "framer-motion";
import { Zap, Shield, Users, Globe, ArrowRight, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export const FeaturesStep = ({ onNext, onBack }) => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Zap,
      title: t("onboarding.features.fast.title", "Lightning Fast"),
      description: t(
        "onboarding.features.fast.description",
        "Send documents for signature in seconds, not hours. Recipients can sign from any device.",
      ),
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
    },
    {
      icon: Shield,
      title: t("onboarding.features.secure.title", "Bank-Level Security"),
      description: t(
        "onboarding.features.secure.description",
        "256-bit encryption, audit trails, and compliance with eIDAS, ESIGN, and UETA regulations.",
      ),
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
    },
    {
      icon: Users,
      title: t("onboarding.features.collaborate.title", "Team Collaboration"),
      description: t(
        "onboarding.features.collaborate.description",
        "Add team members, create templates, and manage documents together seamlessly.",
      ),
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      icon: Globe,
      title: t("onboarding.features.anywhere.title", "Sign Anywhere"),
      description: t(
        "onboarding.features.anywhere.description",
        "In-person signing, remote signing, or signing links - flexibility for every situation.",
      ),
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="max-w-4xl mx-auto px-4"
    >
      <div className="text-center mb-12">
        <motion.h2
          variants={fadeInUp}
          className="text-3xl sm:text-4xl font-bold text-foreground mb-4"
        >
          {t(
            "onboarding.features.title",
            "Everything you need to get documents signed",
          )}
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
          {t(
            "onboarding.features.subtitle",
            "Powerful features that make document signing a breeze",
          )}
        </motion.p>
      </div>

      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
      >
        {features.map((feature) => (
          <motion.div
            key={feature.title}
            variants={fadeInUp}
            whileHover={{ scale: 1.02 }}
            className="p-6 rounded-xl border bg-card hover:shadow-lg transition-shadow"
          >
            <div
              className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center mb-4",
                feature.bgColor,
              )}
            >
              <feature.icon className={cn("w-6 h-6", feature.color)} />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {feature.title}
            </h3>
            <p className="text-muted-foreground">{feature.description}</p>
          </motion.div>
        ))}
      </motion.div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          {t("common.back", "Back")}
        </Button>
        <Button onClick={onNext} className="gap-2">
          {t("common.continue", "Continue")}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

export default FeaturesStep;
